import React from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as ReactDOM from 'react-dom/client';
import { ElementType } from '@/modules/types/element';
import { videoMimeTypes } from '@/modules/types/videoPlayer';
import config from '@/lib/config/index';
import { resolveAssetUrl } from '@/lib/util';
import { FailBehaviour, QuestionResponseType } from '@/modules/types/choice';
import { QuizAttemptResponseType, QuizDataType } from '@/modules/types/quiz';
import { IQuizSetup } from '@/pages/Quiz/components/quizSetup';

export const CONFIG_SANITIZE_HTML = {
  FORCE_BODY: true,
  ADD_TAGS: ['iframe', 'style', 'script'],
};

export const REACT_TOOLTIP_PREFIX = 'r-tooltip';

export function contentTooltip(content: string): string {
  if (!content) return '';

  const regexpImageMap = /title="tip-([0-9]{1,2})"/;
  let match = content.match(regexpImageMap);
  do {
    if (match && match[1]) {
      content = content.replace(
        regexpImageMap,
        ` data-tooltip-id='${REACT_TOOLTIP_PREFIX}-1-${match[1]}'`,
      );
    }
    // eslint-disable-next-line no-cond-assign
  } while ((match = content.match(regexpImageMap)) !== null);

  const regexpInline = /id="tip-([0-9]{1,2})"/;
  match = content.match(regexpInline);
  do {
    if (match && match[1]) {
      content = content.replace(
        regexpInline,
        ` data-tooltip-id='${REACT_TOOLTIP_PREFIX}-1-${match[1]}'`,
      );
    }
    // eslint-disable-next-line no-cond-assign
  } while ((match = content.match(regexpInline)) !== null);
  return content;
}

export function renderButtonTooltip(elements: ElementType[]) {
  return elements.map((element: ElementType, index) => {
    if (element.elementMetadata.type === 2) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          className="img-btn"
          key={`button-tooltip-${element.elementMetadata.position}`}
          data-tooltip-id={`${REACT_TOOLTIP_PREFIX}-${element.elementMetadata.type}-${element.elementMetadata.position}`}
        >
          {element.name}
        </a>
      );
    }
    return <span key={`button-tooltip-${element.elementMetadata.position}`} />;
  });
}

export function setTipBar(
  tipBarRef: React.RefObject<HTMLDivElement>,
  isMobileView = false,
) {
  const tipBar = tipBarRef.current;
  if (tipBar) {
    // The padding below shifts the bar sideways so the tip buttons clear a
    // floated side image — desktop-only geometry. On mobile the image is stacked
    // full-width, so that offset (image width + 15) is roughly the whole
    // viewport and pushes the bar off screen. Fall through to the reset branch
    // instead, which also clears any padding left over from a desktop render.
    const imgContainer = isMobileView
      ? null
      : (document.querySelector(
          '#content-container .img_container',
        ) as HTMLElement);
    if (imgContainer !== null) {
      const paddingTipBar = imgContainer.offsetWidth + 15;
      if (imgContainer.offsetLeft < 100) {
        // left
        tipBar.style.paddingLeft = `${paddingTipBar}px`;
        tipBar.style.paddingRight = '0px';
      } else {
        // right
        tipBar.style.paddingLeft = '0px';
        tipBar.style.paddingRight = `${paddingTipBar}px`;
      }
    } else {
      tipBar.style.paddingLeft = '0px';
      tipBar.style.paddingRight = '0px';
    }
  }
}

/**
 * LMS2-5175 tablet band: mobile-fix blocks are authored at max-width:767px;
 * a data migration widens converted content with this second arm (min-height
 * keeps landscape phones out). The chrome half lives in editor.scss behind
 * `module-viewer--tabletfix`, set by PlayerUiView per COURSE so frames
 * without fixes don't flip it mid-course; unarmed content renders exactly
 * as before.
 */
export const TABLET_MEDIA_ARM =
  'only screen and (min-width:505px) and (max-width:1023px) and (min-height:505px)';

export function hasTabletArmedContent(
  frames: { content?: string }[] | undefined,
): boolean {
  // exact arm only — authored CSS with some other 1023px query must not match
  return (frames ?? []).some(
    (frame) =>
      frame.content?.includes('data-mobile-fix') &&
      frame.content.includes(TABLET_MEDIA_ARM),
  );
}

const parses = (script: string): boolean => {
  try {
    // eslint-disable-next-line no-new, no-new-func
    new Function(script);
    return true;
  } catch {
    return false;
  }
};

const appendScript = (id: string, text: string): HTMLScriptElement => {
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.id = id;
  scriptElement.text = text;
  document.body.appendChild(scriptElement);
  return scriptElement;
};

export function insertUserScript(frameScript: string[] | null | undefined) {
  if (frameScript) {
    frameScript.forEach((contentScript, index) => {
      try {
        const strippedScript = contentScript
          .split(/<\/?script[^>]*>/i)
          .join('');
        // Some stored scripts carry SQL-doubled quotes. Collapse them only when the script won't parse as-is, so a real '' survives.
        const cleanedScript = parses(strippedScript)
          ? strippedScript
          : strippedScript.split(/(?<!\\)''/).join("'");

        const id = `ela-user-module-script-${index}`;
        const scriptElement = appendScript(
          id,
          `document.currentScript.dataset.ran = '1';${cleanedScript}`,
        );
        // A top-level return, or a let/const name already declared by an earlier run, stops a script before its first line. Then run it inside a function.
        if (!scriptElement.dataset.ran) {
          scriptElement.remove();
          appendScript(
            id,
            `(function () {\n${cleanedScript}\n}).call(window);`,
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to insert user script ${index}:`, error);
      }
    });
  }
}

export function updatePlayPauseIcon(media: any) {
  if (media) {
    media.classList.toggle('play');
  }
  const customMedia = document.querySelectorAll("div[name='custom_media']");
  customMedia.forEach((customMediaElement, index) => {
    if (media !== customMediaElement) {
      customMediaElement.classList.remove('play');
    }
  });
  // reset the voice over button
  const playButton = document.getElementsByClassName('sound_on');
  const pauseButton = document.getElementsByClassName('sound_off');
  if (playButton.length > 0 && pauseButton.length > 0) {
    playButton[0].setAttribute('style', 'display:none');
    pauseButton[0].setAttribute('style', 'display:block');
  }
}

export function getExtension(filename: any) {
  return filename ? filename.split('.').pop() : 'mp4';
}

export function getVideoOptions(
  videos: any,
  frameId: string,
  mountType = 'embed',
) {
  const videoOptions: any = [];
  if (mountType === 'bgvideo') {
    const poster = `${config.learningModuleTemplatePath}images/video/play-button.svg`;
    const posterEnd = `${config.learningModuleTemplatePath}images/video/replay-button.svg`;
    const options: any = {
      autoSetup: false,
      styleId: 'bgvideo',
      autoplay: false,
      bigPlayButton: false,
      style: '',
      bgVideoTitle: videos.bgVideoTitle ? videos.bgVideoTitle : '',
      controls: true,
      controlBar: {
        pictureInPictureToggle: false,
      },
      poster,
      posterEnd,
      responsive: true,
      mountType,
      sources: [
        {
          src: encodeURI(
            `${resolveAssetUrl({
              assetBasePath: videos.assetBasePath,
              tenantId: videos.tenantId,
              entity: 'elearnings',
              entityId: videos.elearningId,
              fileType: 'videos',
              fileName: videos.bgVideo,
            })}`,
          ),
          type: videoMimeTypes[getExtension(videos?.bgVideo)],
        },
      ],
    };
    videoOptions.push(options);
  } else {
    videos.forEach((videoItem: any) => {
      const videoSource = videoItem.getAttribute('data-src')
        ? videoItem.getAttribute('data-src')
        : videoItem.getAttribute('src');
      const height = videoItem.getAttribute('data-h');
      const width = videoItem.getAttribute('data-w');
      const ratio = height && width ? (height / width) * 100 : 0;
      let options: any = {
        frameId,
        autoSetup: false,
        autoplay: mountType === 'popup',
        controls: true,
        controlBar: {
          pictureInPictureToggle: false,
        },
        responsive: true,
        bgVideoTitle: '',
        mountType,
        style: `${ratio ? `width:100%;` : ``}${
          videoItem.getAttribute('style') ? videoItem.getAttribute('style') : ''
        }`,
        fluid: !!(
          !videoItem.getAttribute('data-h') && !videoItem.getAttribute('data-w')
        ),
        styleId: videoItem.getAttribute('id'),
        poster: videoItem.getAttribute('poster')
          ? videoItem.getAttribute('poster')
          : '',
        sources: [
          {
            src: videoSource,
            type: videoMimeTypes[getExtension(videoSource)],
          },
        ],
      };

      if (videoItem.getAttribute('data-h')) {
        options = { ...options, height: videoItem.getAttribute('data-h') };
      }

      if (videoItem.getAttribute('data-w')) {
        options = { ...options, width: videoItem.getAttribute('data-w') };
      }

      if (mountType === 'popup') {
        options = {
          ...options,
          inactivityTimeout: 0,
          bigPlayButton: false,
          label: document.querySelector(`#${videoItem.getAttribute('id')}`)
            ?.textContent
            ? document.querySelector(`#${videoItem.getAttribute('id')}`)
                ?.textContent
            : '',
        };
      }

      videoOptions.push(options);
    });
  }

  return videoOptions;
}

// Match and Order type questions do not have image
export function showQuestionImage(responseType: QuestionResponseType): boolean {
  return ![QuestionResponseType.match, QuestionResponseType.order].includes(
    responseType,
  );
}

export function processRetryTimeInSeconds(
  retryPeriodValue: number,
  retryPeriodType: FailBehaviour,
) {
  const multiplicator = retryPeriodType > 0 ? 86400 : 3600; // 86400 -> secs in a day, 3600 -> secs in an hour
  return retryPeriodValue * multiplicator; // retry period in seconds
}

export function processRetryTimeMessage(remainingTimeInMinutes: number) {
  const isTimeDays = remainingTimeInMinutes > 1440; // 1440 mins in a day
  const remainingHours = Math.trunc(remainingTimeInMinutes / 60); // 60 mins in an hour
  const displayMessage = isTimeDays
    ? `${Math.floor(remainingTimeInMinutes / 1440)} days`
    : `${remainingHours} hours ${remainingTimeInMinutes % 60} minutes`;

  return `You have used up all your available attempts for this assessment. Please try again in ${displayMessage}.`;
}

export function breakdownQuizMetadataValues(
  quizData: QuizDataType | undefined,
  defaultQuizValues: IQuizSetup,
) {
  return {
    name: quizData?.name ?? defaultQuizValues.name,
    compulsoryNo:
      quizData?.quizMetadata.compulsoryNo !== undefined
        ? quizData.quizMetadata.compulsoryNo
        : defaultQuizValues.compulsoryNo,
    questionsAsked:
      quizData?.quizMetadata.questionNo !== undefined
        ? quizData?.quizMetadata.questionNo
        : defaultQuizValues.questionsAsked,
    passMark:
      quizData?.quizMetadata.pass !== undefined
        ? quizData?.quizMetadata.pass
        : defaultQuizValues.passMark,
    correctFeedback:
      quizData?.quizMetadata.showAnswerFeedback !== undefined
        ? quizData?.quizMetadata.showAnswerFeedback
        : defaultQuizValues.correctFeedback,
    showAnswer:
      quizData?.quizMetadata.showCorrect !== undefined
        ? quizData?.quizMetadata.showCorrect
        : defaultQuizValues.showAnswer,
    scormHideRedo: quizData?.scormFeedbackMetadata.hideRedoBtn
      ? quizData?.scormFeedbackMetadata.hideRedoBtn
      : defaultQuizValues.scormHideRedo,
    responseTracking:
      quizData?.quizMetadata.lmsTrackingEnable !== undefined
        ? quizData?.quizMetadata.lmsTrackingEnable
        : defaultQuizValues.responseTracking,
    attemptLimit:
      quizData?.quizMetadata.attemptLimit !== undefined &&
      quizData?.quizMetadata.attemptLimit !== null &&
      quizData?.quizMetadata.attemptLimit > 0
        ? quizData?.quizMetadata.attemptLimit
        : defaultQuizValues.attemptLimit,
    failBehaviour:
      quizData?.quizMetadata.failBehaviour !== undefined
        ? quizData?.quizMetadata.failBehaviour
        : defaultQuizValues.failBehaviour,
    retryPeriodValue:
      quizData?.quizMetadata.retryPeriodValue !== undefined
        ? quizData?.quizMetadata.retryPeriodValue
        : defaultQuizValues.retryPeriodValue,
    retryPeriodType:
      quizData?.quizMetadata.retryPeriodType !== undefined
        ? quizData?.quizMetadata.retryPeriodType
        : defaultQuizValues.retryPeriodType,
  };
}

export function getTimeDifference(lastQuizAttemptTime: number | null) {
  if (lastQuizAttemptTime === null) return 0;

  const lastAttemptTimeInMinutes =
    new Date(lastQuizAttemptTime * 1000).getTime() / 60000; // 60000 miliseconds to minutes, 1000 seconds to miliseconds
  const DateNowInMinutes = Date.now() / 60000; // 60000 miliseconds to minutes
  return DateNowInMinutes - lastAttemptTimeInMinutes;
}

export function processFailBehaviourMessage(
  quiz: QuizDataType,
  quizGetAttemptData: QuizAttemptResponseType,
  restartHandler: () => void,
) {
  const assessmentButton = document.getElementById('do-assessment');
  const { quizMetadata } = quiz;

  // only do something if assessment button exist & feature is enabled.
  if (
    !assessmentButton ||
    !quizMetadata.lmsTrackingEnable ||
    !quizGetAttemptData.isAttemptFeatureEnabled
  )
    return;

  const { attemptLimit, failBehaviour } = quizMetadata;

  const { remainingQuizAttempt } = quizGetAttemptData;
  const remainingAttempt = remainingQuizAttempt ?? 0;

  const failMessage =
    'You have not passed this course and have no attempts remaining.';
  const restartMessage =
    'You have used up all your available attempts for this assessment. Please restart this course to try again.';

  if (remainingAttempt > 0) {
    const attemptNo = attemptLimit - remainingAttempt + 1;
    const attemptsLeftMessage = `You are about to begin attempt ${attemptNo} of ${attemptLimit} attempts.`;

    const content = document.createElement('p');
    content.innerText = attemptsLeftMessage;
    assessmentButton.parentElement?.appendChild(content);
  } else {
    const assessmentRoot = ReactDOM.createRoot(assessmentButton.parentElement);

    switch (failBehaviour) {
      case FailBehaviour.restart: // failbehaviour = 0 // restart module
        assessmentRoot.render(
          <>
            <button type="button" onClick={restartHandler} id="do-assessment">
              Restart Course
            </button>
            <p>{restartMessage}</p>
          </>,
        );
        break;
      case FailBehaviour.fail: // failbehaviour = 1 // fail module
        assessmentRoot.render(<p>{failMessage}</p>);
        break;

      case FailBehaviour.retry:
        {
          // failbehaviour = 2 // retry in x time
          const retryTimeInMinutes =
            processRetryTimeInSeconds(
              quizMetadata.retryPeriodValue,
              quizMetadata.retryPeriodType,
            ) / 60;

          const timeDifferenceInMinutes = getTimeDifference(
            quizGetAttemptData.lastQuizAttemptTime,
          );
          if (
            quizGetAttemptData.lastQuizAttemptTime !== null &&
            timeDifferenceInMinutes < retryTimeInMinutes
          ) {
            const remainingTimeInMinutes = Math.trunc(
              retryTimeInMinutes - timeDifferenceInMinutes,
            );
            const retryMessage = processRetryTimeMessage(
              remainingTimeInMinutes,
            );
            assessmentRoot.render(retryMessage);
          }
        }
        break;

      default:
        break;
    }
  }
}
